import { useState, useCallback, memo, forwardRef } from 'react';
import { cn, Cross, Popup } from '@divlab/divanui';

import Img from '@UI/Img';
import Link from '@Navigation/Link';
import styles from './MechanismPopup.module.css';

import type { Parameter } from '@Pages/PageProduct';
import type { HTMLAttributes, MouseEvent } from 'react';

export interface MechanismPopupProps extends HTMLAttributes<HTMLDivElement> {
  className?: string;
  visible?: boolean;
  parameter?: Parameter;
  onClose?: (e: MouseEvent) => void;
}

const MechanismPopup = forwardRef<HTMLDivElement, MechanismPopupProps>((props, ref) => {
  const { visible, parameter, className, onClose, ...restProps } = props;
  const [hovered, setHovered] = useState(false);

  const handleMouseEnterImage = useCallback(() => {
    setHovered(true);
  }, []);

  const handleMouseLeaveImage = useCallback(() => {
    setHovered(false);
  }, []);

  const handleClick = useCallback(() => {
    setHovered(true);
  }, []);

  return (
    <Popup {...restProps} ref={ref} className={cn(styles.popup, className)} visible={visible}>
      <div className={styles.container}>
        <Cross className={styles.iconClose} view='default' size='xs' onClick={onClose} />

        <div className={styles.wrapperImage}>
          <Img
            src={hovered && parameter.image ? parameter.image : parameter.preview}
            className={styles.img}
            onMouseEnter={handleMouseEnterImage}
            onMouseLeave={handleMouseLeaveImage}
          />
          {!hovered && parameter.image && <div className={styles.iconPlay} onClick={handleClick} />}
        </div>

        <div className={styles.content}>
          <div className={styles.title}>{parameter.title}</div>
          {parameter.text && (
            <div className={styles.text}>
              {parameter.text}{' '}
              {parameter.link && (
                <Link view='primary' to={parameter.link.href} underlined>
                  {parameter.link.text}
                </Link>
              )}
            </div>
          )}
        </div>
      </div>
    </Popup>
  );
});

MechanismPopup.displayName = 'MechanismPopup';

export default memo(MechanismPopup);
